import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type {
  LeadershipRecordItem,
  LeadershipRecordSourceType,
  LeadershipRecordVisibility,
} from "@/lib/behavior/viewModels/buildLeadershipRecordItems";
import { isLeaderVisibleRecordItem } from "@/lib/behavior/viewModels/buildLeadershipRecordItems";

type TimelineFilter =
  | "all"
  | "evidence"
  | "reflections"
  | "coach_notes"
  | "bi_observations"
  | "graph_connected"
  | "leader_safe"
  | "coach_only";

interface LeadershipRecordTimelineProps {
  items: LeadershipRecordItem[];
  surface?: "coach" | "leader";
}

const SOURCE_LABELS: Record<LeadershipRecordSourceType, string> = {
  bi_observation: "BI observation",
  context_graph_node: "Connected context",
  leader_memory_event: "Memory evidence",
  practice_summary: "Practice summary",
  transcript_evidence: "Transcript evidence",
  reflection: "Reflection",
  coach_note: "Coach note",
  shared_summary: "Shared summary",
};

const VISIBILITY_LABELS: Record<LeadershipRecordVisibility, string> = {
  coach_only: "Coach-only",
  coach_reviewed: "For coach review",
  leader_safe: "Leader-safe",
  not_shareable_yet: "Not shareable yet",
};

const FILTERS: Array<{ value: TimelineFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "evidence", label: "Evidence-backed moments" },
  { value: "reflections", label: "Reflections" },
  { value: "coach_notes", label: "Coach notes" },
  { value: "bi_observations", label: "BI observations" },
  { value: "graph_connected", label: "Has related context" },
  { value: "leader_safe", label: "Leader-safe" },
  { value: "coach_only", label: "Coach-only" },
];

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;

type PageSize = typeof PAGE_SIZE_OPTIONS[number];

function formatRecordDate(value?: string) {
  if (!value) return "Date not available";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return format(parsed, "MMM d, yyyy");
}

function confidenceLabel(value: LeadershipRecordItem["confidence"]) {
  if (!value) return null;
  return `${value[0].toUpperCase()}${value.slice(1)} confidence`;
}

function evidenceLabel(count: number | undefined) {
  if (!count) return null;
  return `${count} evidence reference${count === 1 ? "" : "s"}`;
}

function countLabel(count: number | undefined, singular: string, plural: string) {
  if (!count) return null;
  return `${count} ${count === 1 ? singular : plural}`;
}

function connectedMomentsLabel(count: number | undefined) {
  const counted = countLabel(count, "related moment", "related moments");
  return counted ? `Connected to ${counted}` : null;
}

function linkedNodesLabel(count: number | undefined) {
  const counted = countLabel(count, "context node", "context nodes");
  return counted ? `Linked to ${counted}` : null;
}

function hasRelatedContext(item: LeadershipRecordItem) {
  return (item.relatedNodeIds?.length || 0) > 0 || (item.relatedMomentIds?.length || 0) > 0;
}

function continuityIndicators(item: LeadershipRecordItem): string[] {
  const relatedMomentCount = item.relatedMomentIds?.length || 0;
  const relatedNodeCount = item.relatedNodeIds?.length || 0;
  const indicators: string[] = [];

  if (relatedMomentCount > 0) indicators.push(`Related moments: ${relatedMomentCount}`);
  if (relatedNodeCount > 0 || item.sourceType === "context_graph_node") indicators.push("Connected context");
  if (relatedMomentCount > 1) indicators.push("Appears across multiple moments");
  if ((item.evidenceCount !== undefined && item.evidenceCount <= 1)
    || item.uncertainty?.some((entry) => /limited|not enough evidence/i.test(entry))) {
    indicators.push("Evidence is limited");
  }
  if (item.visibility === "coach_reviewed" || item.visibility === "not_shareable_yet") {
    indicators.push("Needs coach review");
  }

  return indicators;
}

const RAW_ID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i;

function humanizeSourceToken(value: string) {
  const readable = value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!readable) return "";
  return `${readable[0].toUpperCase()}${readable.slice(1).toLowerCase()}`;
}

function readableEvidenceSource(source: string) {
  const trimmed = source.replace(/\s+/g, " ").trim();
  if (!trimmed) return null;

  const [sourceName, ...detailParts] = trimmed.split(":");
  const detail = detailParts.join(":").trim();
  const readableSourceName = humanizeSourceToken(sourceName);

  if (!detail || RAW_ID_PATTERN.test(detail) || /^[a-z_]+-\d+$/i.test(detail)) {
    return readableSourceName;
  }

  return `${readableSourceName}: ${humanizeSourceToken(detail)}`;
}

function sourcePathLabel(sourceType: LeadershipRecordSourceType) {
  switch (sourceType) {
    case "leader_memory_event":
      return "Recorded in the leadership record as memory evidence.";
    case "bi_observation":
      return "Created from reviewed behavioral evidence.";
    case "context_graph_node":
      return "Connected from approved context already linked to this leader.";
    case "practice_summary":
      return "Created from saved coach practice or session summary material.";
    case "transcript_evidence":
      return "Created from reviewed transcript evidence.";
    case "reflection":
      return "Created from a leader reflection.";
    case "coach_note":
      return "Created from a private coach note.";
    case "shared_summary":
      return "Created from a shared coaching summary.";
    default:
      return "Created from approved coaching record evidence.";
  }
}

function primarySourceLabel(item: LeadershipRecordItem) {
  const source = visibleEvidenceSources(item)
    .map(readableEvidenceSource)
    .find((entry): entry is string => Boolean(entry));
  return source || SOURCE_LABELS[item.sourceType];
}

function readableWhySurfaced(item: LeadershipRecordItem) {
  if (!item.whySurfaced) return "No additional surfacing note was recorded for this item.";
  return item.whySurfaced
    .replace(/\bleader memory event\b/gi, "memory evidence")
    .replace(RAW_ID_PATTERN, "")
    .replace(/\s+/g, " ")
    .trim();
}

function visibleEvidenceSources(item: LeadershipRecordItem) {
  const sources = item.evidenceSources || [];
  if (item.visibility === "leader_safe") {
    return sources.filter((source) => !/\bcoach[_ -]?only\b/i.test(source));
  }
  return sources;
}

function itemMatchesFilter(item: LeadershipRecordItem, filter: TimelineFilter) {
  switch (filter) {
    case "evidence":
      return item.sourceType === "transcript_evidence"
        || item.sourceType === "practice_summary"
        || item.sourceType === "leader_memory_event";
    case "reflections":
      return item.sourceType === "reflection" || item.sourceType === "shared_summary";
    case "coach_notes":
      return item.sourceType === "coach_note";
    case "bi_observations":
      return item.sourceType === "bi_observation";
    case "graph_connected":
      return hasRelatedContext(item) || item.sourceType === "context_graph_node";
    case "leader_safe":
      return item.visibility === "leader_safe";
    case "coach_only":
      return item.visibility === "coach_only";
    case "all":
    default:
      return true;
  }
}

export function LeadershipRecordTimeline({
  items,
  surface = "coach",
}: LeadershipRecordTimelineProps) {
  const [filter, setFilter] = useState<TimelineFilter>("all");
  const [pageSize, setPageSize] = useState<PageSize>(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const filteredItems = surface === "leader" ? items.filter(isLeaderVisibleRecordItem) : items;
  const sortedItems = useMemo(() => (
    [...filteredItems].sort((a, b) =>
      String(b.occurredAt || b.createdAt || "").localeCompare(String(a.occurredAt || a.createdAt || ""))
    )
  ), [filteredItems]);
  const visibleItems = sortedItems.filter((item) => itemMatchesFilter(item, filter));
  const pageCount = Math.max(1, Math.ceil(visibleItems.length / pageSize));
  const currentPageForView = Math.min(currentPage, pageCount);
  const firstItemIndex = (currentPageForView - 1) * pageSize;
  const paginatedItems = visibleItems.slice(firstItemIndex, firstItemIndex + pageSize);
  const visibleRangeStart = visibleItems.length === 0 ? 0 : firstItemIndex + 1;
  const visibleRangeEnd = Math.min(firstItemIndex + pageSize, visibleItems.length);
  const showPaginationControls = visibleItems.length > PAGE_SIZE_OPTIONS[0];

  return (
    <Card className="border-border/60 bg-background/85" role="region" aria-label="Leadership Record timeline">
      <CardHeader className="space-y-3 pb-3">
        <div className="space-y-1">
          <CardTitle className="text-base">Leadership Record</CardTitle>
          <p className="text-sm leading-6 text-muted-foreground">
            A coach-governed record of evidence-backed leadership moments across time.
          </p>
        </div>
        {items.length > 0 ? (
          <div className="flex flex-wrap gap-2" aria-label="Leadership Record filters">
            {FILTERS.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={filter === option.value ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setFilter(option.value);
                  setCurrentPage(1);
                  setOpenItemId(null);
                }}
              >
                {option.label}
              </Button>
            ))}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4 p-4 pt-0">
        {items.length === 0 ? (
          <p className="rounded-md border border-border/60 bg-muted/10 p-4 text-sm leading-6 text-muted-foreground">
            No leadership record items yet. Once meeting evidence, reflections, coach notes, or memory events are available, they will appear here for coach review.
          </p>
        ) : visibleItems.length === 0 ? (
          <p className="rounded-md border border-border/60 bg-muted/10 p-4 text-sm leading-6 text-muted-foreground">
            No leadership record items match this filter.
          </p>
        ) : (
          <>
          {showPaginationControls ? (
            <div className="flex flex-col gap-3 rounded-md border border-border/60 bg-muted/10 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Records per page</p>
                <p className="text-xs text-muted-foreground">
                  Showing {visibleRangeStart}-{visibleRangeEnd} of {visibleItems.length}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1" aria-label="Leadership Record page size">
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <Button
                      key={option}
                      type="button"
                      variant={pageSize === option ? "default" : "outline"}
                      size="sm"
                      className="h-8 min-w-10 px-2"
                      onClick={() => {
                        setPageSize(option);
                        setCurrentPage(1);
                        setOpenItemId(null);
                      }}
                    >
                      {option}
                    </Button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={currentPageForView === 1}
                    onClick={() => {
                      setCurrentPage(Math.max(1, currentPageForView - 1));
                      setOpenItemId(null);
                    }}
                  >
                    Previous
                  </Button>
                  <span className="min-w-16 text-center text-xs text-muted-foreground">
                    Page {currentPageForView} of {pageCount}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={currentPageForView === pageCount}
                    onClick={() => {
                      setCurrentPage(Math.min(pageCount, currentPageForView + 1));
                      setOpenItemId(null);
                    }}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          <ol className="space-y-3">
            {paginatedItems.map((item) => {
              const date = formatRecordDate(item.occurredAt || item.createdAt);
              const confidence = confidenceLabel(item.confidence);
              const evidence = evidenceLabel(item.evidenceCount);
              const relatedNodes = linkedNodesLabel(item.relatedNodeIds?.length);
              const relatedMoments = connectedMomentsLabel(item.relatedMomentIds?.length);
              const itemHasRelatedContext = hasRelatedContext(item);
              const continuity = continuityIndicators(item);
              const sources = visibleEvidenceSources(item);
              const readableSources = sources
                .map(readableEvidenceSource)
                .filter((source): source is string => Boolean(source));
              const isOpen = openItemId === item.id;
              return (
                <li key={item.id} className="rounded-md border border-border/60 bg-muted/10 p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{date}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{SOURCE_LABELS[item.sourceType]}</Badge>
                      <Badge variant="outline">{VISIBILITY_LABELS[item.visibility]}</Badge>
                      {confidence ? <Badge variant="outline">{confidence}</Badge> : null}
                    </div>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-foreground">{item.observedFact}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {evidence ? <span>{evidence}</span> : null}
                    {relatedNodes ? <span>{relatedNodes}</span> : null}
                    {relatedMoments ? <span>{relatedMoments}</span> : null}
                    {continuity.map((indicator) => (
                      <span key={indicator}>{indicator}</span>
                    ))}
                    {itemHasRelatedContext ? (
                      <span>May be part of an emerging thread for coach review</span>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      aria-expanded={isOpen}
                      aria-controls={`leadership-record-lineage-${item.id}`}
                      onClick={() => setOpenItemId(isOpen ? null : item.id)}
                    >
                      Why this appears for {item.title}
                    </Button>
                  </div>

                  {item.uncertainty && item.uncertainty.length > 0 ? (
                    <div className="mt-3 rounded-md border border-border/50 bg-background/70 p-3">
                      <p className="text-xs font-medium uppercase text-muted-foreground">
                        Uncertainty
                      </p>
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        {item.uncertainty.map((entry) => (
                          <li key={entry} className="text-sm leading-6 text-muted-foreground">
                            {entry}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {isOpen ? (
                    <div
                      id={`leadership-record-lineage-${item.id}`}
                      role="region"
                      aria-label={`Evidence lineage for ${item.title}`}
                      className="mt-3 space-y-4 rounded-md border border-border/50 bg-background/80 p-3"
                    >
                      <div className="space-y-1">
                        <h4 className="text-sm font-semibold text-foreground">Why this appears</h4>
                        <div className="rounded-md border border-border/50 p-3">
                          <p className="text-xs font-medium uppercase text-muted-foreground">Why this surfaced</p>
                          <p className="mt-2 text-sm leading-6 text-muted-foreground">
                            {readableWhySurfaced(item)}
                          </p>
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-2 rounded-md border border-border/50 p-3">
                          <p className="text-xs font-medium uppercase text-muted-foreground">Source</p>
                          <p className="text-sm text-foreground">{primarySourceLabel(item)}</p>
                          <p className="text-sm text-muted-foreground">{sourcePathLabel(item.sourceType)}</p>
                          {evidence ? <p className="text-sm text-muted-foreground">{evidence}</p> : null}
                        </div>

                        <div className="space-y-2 rounded-md border border-border/50 p-3">
                          <p className="text-xs font-medium uppercase text-muted-foreground">Evidence sources</p>
                          {readableSources.length > 0 ? (
                            <ul className="list-disc space-y-1 pl-5">
                              {readableSources.map((source) => (
                                <li key={source} className="text-sm leading-6 text-muted-foreground">
                                  {source}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No evidence sources were recorded for this item.
                            </p>
                          )}
                          {evidence ? (
                            <p className="text-sm text-foreground">{evidence}</p>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No evidence count was recorded for this item.
                            </p>
                          )}
                          {item.occurredAt ? (
                            <p className="text-sm text-muted-foreground">
                              Occurred {formatRecordDate(item.occurredAt)}
                            </p>
                          ) : null}
                          {item.createdAt ? (
                            <p className="text-sm text-muted-foreground">
                              Created {formatRecordDate(item.createdAt)}
                            </p>
                          ) : null}
                          {confidence ? <p className="text-sm text-muted-foreground">{confidence}</p> : null}
                        </div>

                        <div className="space-y-2 rounded-md border border-border/50 p-3">
                          <p className="text-xs font-medium uppercase text-muted-foreground">Visibility</p>
                          <p className="text-sm text-foreground">{VISIBILITY_LABELS[item.visibility]}</p>
                        </div>

                        <div className="space-y-2 rounded-md border border-border/50 p-3">
                          <p className="text-xs font-medium uppercase text-muted-foreground">Connected context</p>
                          {item.relatedNodeIds?.length ? (
                            <p className="text-sm text-muted-foreground">
                              {linkedNodesLabel(item.relatedNodeIds.length)}: {item.relatedNodeIds.join(", ")}
                            </p>
                          ) : null}
                          {item.relatedMomentIds?.length ? (
                            <p className="text-sm text-muted-foreground">
                              {connectedMomentsLabel(item.relatedMomentIds.length)}: {item.relatedMomentIds.join(", ")}
                            </p>
                          ) : null}
                          {!itemHasRelatedContext ? (
                            <p className="text-sm text-muted-foreground">
                              No related context was recorded for this item.
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div className="space-y-2 rounded-md border border-border/50 p-3">
                        <p className="text-xs font-medium uppercase text-muted-foreground">What is uncertain</p>
                        {item.uncertainty && item.uncertainty.length > 0 ? (
                          <ul className="list-disc space-y-1 pl-5">
                            {item.uncertainty.map((entry) => (
                              <li key={entry} className="text-sm leading-6 text-muted-foreground">
                                {entry}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No uncertainty notes were recorded for this item.
                          </p>
                        )}
                      </div>

                      <p className="rounded-md border border-border/50 bg-muted/10 p-3 text-sm leading-6 text-muted-foreground">
                        This record item is for coach review and should not be treated as a conclusion about the leader.
                      </p>

                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
          </>
        )}
      </CardContent>
    </Card>
  );
}
